<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SegmentedControl from 'dashboard/components-next/segmented-control/SegmentedControl.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import RenameCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/RenameCampaignDialog.vue';
import MergeCampaignsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/MergeCampaignsDialog.vue';
import CampaignMergeBar from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignMergeBar.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import ConfirmStopCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmStopCampaignDialog.vue';
import CampaignSendsDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignSendsDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const router = useRouter();
const getters = useStoreGetters();

const selectedCampaign = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const renameCampaignDialogRef = ref(null);
const mergeCampaignsDialogRef = ref(null);
const confirmDeleteCampaignDialogRef = ref(null);
const confirmStopCampaignDialogRef = ref(null);
const campaignSendsDialogRef = ref(null);

const statusFilter = ref('');
const selectedIds = ref([]);
const statusFilterOptions = computed(() => [
  { value: '', label: t('CAMPAIGN.WHATSAPP.FILTER.ALL') },
  { value: 'active', label: t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED') },
  { value: 'processing', label: t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING') },
  { value: 'completed', label: t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED') },
  { value: 'cancelled', label: t('CAMPAIGN.SMS.CARD.STATUS.CANCELLED') },
]);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

// The store getter returns campaigns sorted oldest-first by id; newest campaigns
// surface first here, then the status filter narrows the list further.
const sortedWhatsAppCampaigns = computed(() =>
  [...(WhatsAppCampaigns.value || [])].sort((a, b) => b.id - a.id)
);

const filteredWhatsAppCampaigns = computed(() => {
  if (!statusFilter.value) return sortedWhatsAppCampaigns.value;
  return sortedWhatsAppCampaigns.value.filter(
    campaign => campaign.campaign_status === statusFilter.value
  );
});

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const hasNoFilterResults = computed(
  () =>
    !hasNoWhatsAppCampaigns.value &&
    !isFetchingCampaigns.value &&
    filteredWhatsAppCampaigns.value.length === 0
);

// A campaign is set up on a page of its own: the template, what fills each variable, the
// spreadsheet and the preview do not fit in a dialog.
const openNewCampaign = () => {
  router.push({ name: 'campaigns_whatsapp_new' });
};

const handleRename = campaign => {
  renameCampaignDialogRef.value.open(campaign);
};

const toggleSelection = campaign => {
  selectedIds.value = selectedIds.value.includes(campaign.id)
    ? selectedIds.value.filter(id => id !== campaign.id)
    : [...selectedIds.value, campaign.id];
};

const clearSelection = () => {
  selectedIds.value = [];
};

const selectedCampaigns = computed(() =>
  filteredWhatsAppCampaigns.value.filter(campaign =>
    selectedIds.value.includes(campaign.id)
  )
);

// A campaign hidden by the filter must not be merged from a bar that no longer shows it.
watch(statusFilter, clearSelection);

const handleMerge = () => {
  mergeCampaignsDialogRef.value.open(selectedCampaigns.value);
};

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};

const handleStop = campaign => {
  selectedCampaign.value = campaign;
  confirmStopCampaignDialogRef.value.dialogRef.open();
};

const handleViewSends = campaign => {
  selectedCampaign.value = campaign;
  campaignSendsDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="openNewCampaign"
  >
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else-if="!hasNoWhatsAppCampaigns">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
        <CampaignMergeBar
          v-if="selectedCampaigns.length"
          :campaigns="selectedCampaigns"
          @merge="handleMerge"
          @clear="clearSelection"
        />
        <span v-else />
        <SegmentedControl
          v-model="statusFilter"
          :options="statusFilterOptions"
        />
      </div>
      <CampaignList
        v-if="!hasNoFilterResults"
        :campaigns="filteredWhatsAppCampaigns"
        is-selectable
        :selected-ids="selectedIds"
        @rename="handleRename"
        @select="toggleSelection"
        @delete="handleDelete"
        @stop="handleStop"
        @view-sends="handleViewSends"
      />
      <p v-else class="py-10 text-sm text-center text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.FILTER.NO_RESULTS') }}
      </p>
    </template>
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <RenameCampaignDialog ref="renameCampaignDialogRef" />
    <MergeCampaignsDialog
      ref="mergeCampaignsDialogRef"
      @merged="clearSelection"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <ConfirmStopCampaignDialog
      ref="confirmStopCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <CampaignSendsDialog
      ref="campaignSendsDialogRef"
      :campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
